import React, { useEffect, useState } from "react";
import {
  fetchHospitalServicesDetailsList,
  addHospitalServicesDetails,
  editHospitalServicesDetails,
  updateHospitalServicesDetails,
  trashHospitalServicesDetails,
} from "../../Adapters/ApiAdapter";
import { baseImage_path } from "../../variables/Variables";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
const unwrap = (res) => res?.data?.data ?? res?.data ?? {};

export const HospitalServicesDetailsList = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchHospitalServicesDetailsList();
      setRows((res.data || []).sort((a, b) => b.id - a.id));
    } catch {
      toast.error("Failed to load hospital services");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditing({ heading: "", content: "", img_path: null });
    setDialogOpen(true);
  };

  const openEdit = async (id) => {
    try {
      const res = await editHospitalServicesDetails(id);
      const d = unwrap(res);
      setEditing({ id: d.id, heading: d.heading, content: d.content, img_path: d.img_path });
      setDialogOpen(true);
    } catch {
      toast.error("Failed to fetch service data");
    }
  };

  const handleSave = async () => {
    if (!editing.heading || !editing.content) {
      toast.error("All fields are required");
      return;
    }
    setSaving(true);
    const formData = new FormData();
    formData.append("heading", editing.heading);
    formData.append("content", editing.content);
    if (editing.img_path instanceof File) formData.append("img_path", editing.img_path);

    try {
      if (editing.id) {
        await updateHospitalServicesDetails(editing.id, formData);
        toast.success("Service updated");
      } else {
        await addHospitalServicesDetails(formData);
        toast.success("Service added");
      }
      setDialogOpen(false);
      load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleTrash = async (id) => {
    if (!window.confirm("Trash this service?")) return;
    try {
      await trashHospitalServicesDetails(id);
      toast.success("Service trashed");
      load();
    } catch {
      toast.error("Failed to trash service");
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "heading", headerName: "Heading", flex: 1 },
    {
      field: "img_path",
      headerName: "Image",
      width: 120,
      renderCell: (params) => (
        params.value ? (
          <img
            src={`${baseImage_path}/${params.value}`}
            alt="service"
            style={{ width: 80, height: 60, objectFit: "cover", borderRadius: 4 }}
          />
        ) : (
          "—"
        )
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => (
        <Box>
          <Button size="small" variant="contained" onClick={() => openEdit(params.id)}>
            Edit
          </Button>
          <Button size="small" color="error" variant="contained" sx={{ ml: 1 }} onClick={() => handleTrash(params.id)}>
            Trash
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box m={2}>
      <ToastContainer />
      <Typography variant="h4" mb={2}>
        Hospital Services – Details
      </Typography>
      <Button variant="contained" onClick={openAdd} sx={{ mb: 2 }}>
        Add Service
      </Button>
      {loading ? <CircularProgress /> : <DataGrid autoHeight rows={rows} columns={columns} pageSize={10} />}          

      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing?.id ? "Edit" : "Add"} Service</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="normal" label="Heading" value={editing?.heading || ""} onChange={(e) => setEditing({ ...editing, heading: e.target.value })} />
          <TextField fullWidth margin="normal" label="Content" multiline minRows={4} value={editing?.content || ""} onChange={(e) => setEditing({ ...editing, content: e.target.value })} />
          <Button variant="contained" component="label" sx={{ mt: 2 }}>
            Upload Image
            <input type="file" hidden accept="image/*" onChange={(e) => {
              const file = e.target.files[0];
              if (file) setEditing({ ...editing, img_path: file });
            }} />
          </Button>
          {editing?.img_path && (
            <Box mt={2}>
              <img src={editing.img_path instanceof File ? URL.createObjectURL(editing.img_path) : `${baseImage_path}/${editing.img_path}`} alt="preview" style={{ width: 120, height: 80, objectFit: "cover", borderRadius: 4 }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? <CircularProgress size={24} /> : "Save"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};